import { makeAutoObservable, runInAction } from "mobx";
import {
  createSwayyCategory,
  flattenCategoryTree,
  getSwayyCategory,
  listSwayyCategoryTree,
  SwayyCategoryDetail,
  SwayyCategoryTreeNode,
  SwayyUpdateCategoryPayload,
  updateSwayyCategory,
  updateSwayyCategoryOptions,
} from "../api/SwayyCategoriesAPI";
import { listSwayyCatalogOptions } from "../api/SwayyOptionsAPI";
import {
  isCancelledSwayyRequest,
  normalizeSwayyApiError,
  SwayyApiError,
} from "../api/swayyApiHelpers";
import { SwayyCatalogLoadState } from "../models/common/SwayyCatalogLoadState";
import { SwayyCatalogOption } from "../models/options/SwayyCatalogOption";
import {
  createCategoryFormSchema,
  zodFirstMessage,
} from "../validation/swayyFormSchemas";

export type SwayyCreateCategoryForm = {
  name: string;
  skuCode: string;
};

export class SwayyCategoriesStore {
  tree: SwayyCategoryTreeNode[] = [];
  categoryDetail: SwayyCategoryDetail | null = null;
  catalogOptions: SwayyCatalogOption[] = [];
  draftOptionIds: number[] = [];
  createForm: SwayyCreateCategoryForm = { name: "", skuCode: "" };
  listState: SwayyCatalogLoadState = "idle";
  detailState: SwayyCatalogLoadState = "idle";
  errorMessage = "";
  isSaving = false;
  searchDraft = "";
  search = "";
  private listAbort?: AbortController;
  private detailAbort?: AbortController;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get flatCategories() {
    const rows = flattenCategoryTree(this.tree);
    const q = this.search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        row.name.toLowerCase().includes(q) ||
        row.skuCode.toLowerCase().includes(q),
    );
  }

  get canSaveOptions(): boolean {
    if (!this.categoryDetail || this.isSaving) return false;
    const current = this.categoryDetail.options
      .map((item) => item.optionId)
      .sort((a, b) => a - b);
    const draft = [...this.draftOptionIds].sort((a, b) => a - b);
    return JSON.stringify(current) !== JSON.stringify(draft);
  }

  get canCreateCategory(): boolean {
    return createCategoryFormSchema.safeParse(this.createForm).success;
  }

  setSearchDraft(value: string): void {
    this.searchDraft = value;
  }

  setSearch(value: string): void {
    this.search = value;
  }

  setDraftOptionIds(ids: number[]): void {
    this.draftOptionIds = ids;
  }

  updateCreateForm<K extends keyof SwayyCreateCategoryForm>(
    key: K,
    value: SwayyCreateCategoryForm[K],
  ): void {
    this.createForm = { ...this.createForm, [key]: value };
  }

  resetCreateForm(): void {
    this.createForm = { name: "", skuCode: "" };
  }

  async loadTree(): Promise<SwayyApiError | null> {
    this.listAbort?.abort();
    const controller = new AbortController();
    this.listAbort = controller;
    this.listState = "loading";
    this.errorMessage = "";
    try {
      const tree = await listSwayyCategoryTree(controller.signal);
      runInAction(() => {
        this.tree = tree;
        this.listState = "ready";
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error)) return null;
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to load categories.",
      );
      runInAction(() => {
        this.listState = "error";
        this.errorMessage = apiError.message;
      });
      return apiError;
    }
  }

  async loadCategory(categoryId: number): Promise<SwayyApiError | null> {
    this.detailAbort?.abort();
    const controller = new AbortController();
    this.detailAbort = controller;
    this.detailState = "loading";
    this.errorMessage = "";
    try {
      const [detail, catalogOptions] = await Promise.all([
        getSwayyCategory(categoryId, controller.signal),
        listSwayyCatalogOptions(controller.signal),
      ]);
      runInAction(() => {
        this.categoryDetail = detail;
        this.catalogOptions = catalogOptions;
        this.draftOptionIds = detail.options.map((item) => item.optionId);
        this.detailState = "ready";
      });
      return null;
    } catch (error) {
      if (isCancelledSwayyRequest(error)) return null;
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to load category.",
      );
      runInAction(() => {
        this.detailState = "error";
        this.errorMessage = apiError.message;
        this.categoryDetail = null;
      });
      return apiError;
    }
  }

  async createCategory(): Promise<{
    categoryId?: number;
    error?: SwayyApiError;
  }> {
    const parsed = createCategoryFormSchema.safeParse(this.createForm);
    if (!parsed.success) {
      return { error: new SwayyApiError(zodFirstMessage(parsed.error)) };
    }
    this.isSaving = true;
    try {
      const created = await createSwayyCategory({
        name: parsed.data.name,
        sku_code: parsed.data.skuCode.toUpperCase(),
      });
      runInAction(() => {
        this.isSaving = false;
        this.resetCreateForm();
      });
      await this.loadTree();
      return { categoryId: created.id };
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to create category.",
      );
      runInAction(() => {
        this.isSaving = false;
      });
      return { error: apiError };
    }
  }

  async updateCategory(
    categoryId: number,
    payload: SwayyUpdateCategoryPayload,
  ): Promise<SwayyApiError | null> {
    this.isSaving = true;
    try {
      const updated = await updateSwayyCategory(categoryId, payload);
      runInAction(() => {
        this.categoryDetail = updated;
        this.isSaving = false;
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to update category.",
      );
      runInAction(() => {
        this.isSaving = false;
      });
      return apiError;
    }
  }

  async saveCategoryOptions(
    categoryId: number,
  ): Promise<SwayyApiError | null> {
    if (!this.canSaveOptions) return null;
    this.isSaving = true;
    try {
      const updated = await updateSwayyCategoryOptions(
        categoryId,
        this.draftOptionIds.map((optionId, index) => ({
          optionId,
          position: index,
          isRequired: true,
        })),
      );
      runInAction(() => {
        this.categoryDetail = updated;
        this.draftOptionIds = updated.options.map((item) => item.optionId);
        this.isSaving = false;
      });
      return null;
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        "Unable to save category options.",
      );
      runInAction(() => {
        this.isSaving = false;
      });
      return apiError;
    }
  }

  dispose(): void {
    this.listAbort?.abort();
    this.detailAbort?.abort();
  }
}
